import { decodeEntities } from '../../lib/decodeEntities'
import AuthorConvertButton from '../../components/crm/AuthorConvertButton'
import { useState, useRef, useEffect, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  FileSearch, ChevronRight, Search, ArrowLeft, SortAsc,
  Bookmark, Check, AlertCircle, Globe, Scale, X, Building2, CalendarRange, Eye, ExternalLink, Filter,
} from 'lucide-react'
import {
  getPatentTopics, browsePatents, searchPatents, compareWithInvention,
  getProjects, getPersonalLists, addListItem, getPatent, getDocuments,
} from '../../services/api'
import type { PatentBrowseResult, PatentSearchResult, TopicFacet, JurisdictionFacet } from '../../services/api'
import { topicFacetComparator } from '../../utils/topicSort'
import { googlePatentsUrl } from '../../utils/patentUtils'
import { disambiguateProjectLabels } from '../../utils/projects'
import { useTimedOut } from '../../hooks/useTimedOut'
import type { PersonalList, NoveltyReport } from '../../types'
import LoadingSkeleton from '../../components/shared/LoadingSkeleton'
import { PatentTranslate } from '../../components/patents/PatentTranslate'
import PatentPreviewPanel from '../../components/patents/PatentPreviewPanel'
import AddPatentToProjectMenu from '../../components/patents/AddPatentToProjectMenu'
import PatentConnectionsPanel from '../../components/graph/PatentConnectionsPanel'
import EmptyState from '../../components/ui/EmptyState'
import PageHeader from '../../components/ui/PageHeader'
import ContextualTip from '../../components/ui/ContextualTip'

function CardAddToList({ patentId, patentTitle }: { patentId: string; patentTitle: string }) {
  const [open, setOpen] = useState(false)
  const [added, setAdded] = useState<string | null>(null)
  const [addError, setAddError] = useState<string | null>(null)
  const ref = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()

  const { data: lists } = useQuery({
    queryKey: ['personal-lists'],
    queryFn: getPersonalLists,
    enabled: open,
  })

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleAdd = async (list: PersonalList) => {
    setAddError(null)
    try {
      await addListItem(list.list_id, {
        resource_id: patentId,
        resource_type: 'patent',
        title: patentTitle,
      })
      setAdded(list.list_id)
      queryClient.invalidateQueries({ queryKey: ['list-items', list.list_id] })
      setTimeout(() => { setAdded(null); setOpen(false) }, 1200)
    } catch (err) {
      // BUG-127: only a 409 means "already in this list" — a benign duplicate we
      // can treat as success. Every other outcome (403/500/network) is a real
      // failure and must be surfaced instead of silently swallowed.
      const e = err as { response?: { status?: number; data?: { error?: string } }; message?: string }
      if (e?.response?.status === 409) {
        setAdded(list.list_id)
        setTimeout(() => { setAdded(null); setOpen(false) }, 1200)
      } else {
        setAddError(e?.response?.data?.error || e?.message || 'Failed to add to list')
      }
    }
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open) }}
        className="p-1.5 text-gray-500 hover:text-iflow-600 flex-shrink-0"
        title="Add to list"
      >
        <Bookmark size={14} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
          {!lists || lists.length === 0 ? (
            <p className="px-3 py-2 text-xs text-gray-500">No lists yet.</p>
          ) : (
            lists.map((l) => (
              <button
                key={l.list_id}
                onClick={(e) => { e.stopPropagation(); handleAdd(l) }}
                className="w-full flex items-center justify-between px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
              >
                <span className="truncate">{l.name}</span>
                {added === l.list_id && <Check size={12} className="text-green-500 flex-shrink-0" />}
              </button>
            ))
          )}
          {addError && (
            <p data-testid="add-list-error" className="px-3 py-2 text-xs text-red-600 border-t border-gray-100">{addError}</p>
          )}
        </div>
      )}
    </div>
  )
}

function MatchBadge({ percent }: { percent: number }) {
  const tone = percent >= 90
    ? 'bg-green-100 text-green-700'
    : percent >= 70
      ? 'bg-iflow-100 text-iflow-700'
      : 'bg-gray-100 text-gray-600'
  return (
    <span data-testid="patent-match-badge" className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0 ${tone}`}>
      {percent}% match
    </span>
  )
}

function PatentCard({ patent, onCompare, onPreview, onInventorClick, matchPercent }: {
  patent: PatentBrowseResult
  onCompare: (id: string) => void
  onPreview: (id: string) => void
  onInventorClick?: (name: string) => void
  matchPercent?: number | null
}) {
  const navigate = useNavigate()

  return (
    <div
      onClick={() => navigate(`/patents/${encodeURIComponent(patent.patent_id)}`)}
      className="bg-white rounded-lg border border-gray-200 p-4 hover:border-iflow-300 transition-colors cursor-pointer"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-mono text-iflow-600 bg-iflow-50 px-1.5 py-0.5 rounded flex-shrink-0">
              {patent.patent_id}
            </span>
            {patent.jurisdiction && (
              <span className="text-[10px] text-gray-500 flex items-center gap-0.5">
                <Globe size={10} /> {patent.jurisdiction}
              </span>
            )}
            {patent.source_lang && patent.source_lang !== 'en' && (
              <span
                data-testid="patent-lang-badge"
                className="text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded uppercase tracking-wide flex-shrink-0"
                title={`Original language: ${patent.source_lang}`}
              >
                {patent.source_lang}
              </span>
            )}
            {matchPercent != null && <MatchBadge percent={matchPercent} />}
          </div>
          <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 mt-1">{patent.title}</h3>
          {patent.inventors && patent.inventors.length > 0 && (
            <div className="text-xs text-gray-500 mt-1 flex items-center gap-1 flex-wrap">
              {patent.inventors.slice(0, 3).map((inv: string, idx: number) => (
                <span key={idx} className="inline-flex items-center">
                  {idx > 0 && ', '}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onInventorClick?.(inv) }}
                    className="text-iflow-600 hover:underline"
                    title={`See other patents by ${inv}`}
                  >{inv}</button>
                  <AuthorConvertButton authorString={inv} sourceDocumentId={patent.patent_id}
                    sourceDocumentTitle={patent.title} sourceType="patent" />
                </span>
              ))}
              {patent.inventors.length > 3 && <span> +{patent.inventors.length - 3} more</span>}
              {patent.assignee && <span className="text-gray-500"> — {patent.assignee}</span>}
            </div>
          )}
          {patent.abstract && (
            <p className="text-xs text-gray-500 mt-2 line-clamp-3">{decodeEntities(patent.abstract)}</p>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <CardAddToList patentId={patent.patent_id} patentTitle={patent.title} />
          <AddPatentToProjectMenu patent={patent} />
          <button
            onClick={(e) => { e.stopPropagation(); onPreview(patent.patent_id) }}
            className="p-1.5 text-gray-500 hover:text-iflow-600"
            title="Preview"
          >
            <Eye size={14} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onCompare(patent.patent_id) }}
            className="p-1.5 text-gray-500 hover:text-iflow-600"
            title="Compare with invention"
          >
            <Scale size={14} />
          </button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-3">
        {patent.filing_date && (
          <span className="text-[10px] text-gray-500">Filed {patent.filing_date}</span>
        )}
        {patent.source_api && (
          <span className="text-[10px] text-gray-500 uppercase">{patent.source_api}</span>
        )}
      </div>
      {patent.topics && patent.topics.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {patent.topics.slice(0, 5).map((t) => (
            <span key={t} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded">
              {t}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

function TopicSidebar({
  topics,
  selectedTopic,
  onSelect,
  breadcrumb,
  onBack,
  isLoading,
  timedOut,
  isError,
  onRetry,
}: {
  topics: TopicFacet[]
  selectedTopic: string
  onSelect: (topic: string) => void
  breadcrumb: string[]
  onBack: () => void
  isLoading: boolean
  /** BUG-VIS-4-2: true once `isLoading` has stayed true past a bounded wait —
   * lets the sidebar fall back to an honest retry state instead of an
   * infinite skeleton. */
  timedOut?: boolean
  isError?: boolean
  onRetry?: () => void
}) {
  // Never spin forever: once we've timed out (or the query genuinely
  // errored) and still have nothing to show, surface a real retry state
  // instead of leaving the skeleton up indefinitely.
  const showStalled = (timedOut || isError) && topics.length === 0
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center gap-2 mb-3">
        <FileSearch size={14} className="text-iflow-500" />
        <h3 className="text-sm font-semibold text-gray-700">Patent Categories</h3>
      </div>
      {breadcrumb.length > 0 && (
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-xs text-iflow-600 hover:text-iflow-700 mb-2 font-medium"
        >
          <ArrowLeft size={12} /> Back
        </button>
      )}
      {breadcrumb.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 text-[10px] text-gray-500 mb-2">
          {breadcrumb.map((b, i) => (
            <span key={i} className="flex items-center gap-1">
              {i > 0 && <ChevronRight size={8} />}
              <span className="truncate max-w-[100px]">{b}</span>
            </span>
          ))}
        </div>
      )}
      {isLoading && !showStalled ? (
        <div className="space-y-2" data-testid="patent-categories-skeleton">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-8 bg-gray-100 rounded animate-pulse" />
          ))}
        </div>
      ) : showStalled ? (
        <div className="text-center py-4" data-testid="patent-categories-stalled">
          <p className="text-xs text-gray-500 mb-2">
            {isError ? 'Categories failed to load.' : 'Categories are taking longer than expected.'}
          </p>
          <button
            type="button"
            onClick={onRetry}
            className="text-xs font-medium text-iflow-600 hover:text-iflow-700 underline"
          >
            Retry
          </button>
        </div>
      ) : (
        <div className="space-y-0.5 max-h-[60vh] overflow-y-auto">
          {[...topics].sort(topicFacetComparator).map((t) => (
            <button
              key={t.topic}
              onClick={() => onSelect(t.topic)}
              className={`w-full flex items-center justify-between px-2 py-1.5 text-xs rounded-md transition-colors ${
                selectedTopic === t.topic
                  ? 'bg-iflow-50 text-iflow-700 font-medium'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span className="truncate">{t.topic}</span>
              <span className="text-[10px] text-gray-500 ml-1 flex-shrink-0">
                {(t.count ?? 0).toLocaleString()}
              </span>
            </button>
          ))}
          {topics.length === 0 && !isLoading && (
            <p className="text-xs text-gray-500 px-2 py-2">No categories found</p>
          )}
        </div>
      )}
    </div>
  )
}

export default function Patents() {
  const [categoriesOpen, setCategoriesOpen] = useState(false)
  useEffect(() => {
    if (!categoriesOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setCategoriesOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [categoriesOpen])
  const [selectedTopic, setSelectedTopic] = useState('')
  const [breadcrumb, setBreadcrumb] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [searchSubmitted, setSearchSubmitted] = useState(false)
  // #4612: "other patents by this inventor" mode. When set, the results query
  // runs an inventor-field search instead of a keyword search.
  const [inventorFilter, setInventorFilter] = useState('')
  // #4612/#4646: "other patents by this assignee" mode — same pattern as
  // inventorFilter, backed by the real server-side assignee search (Stream 1).
  // Distinct from `selectedAssignees` below, which is an unrelated client-side
  // checkbox facet filter over the currently loaded page.
  const [assigneeSearchFilter, setAssigneeSearchFilter] = useState('')
  // BUG-155/BUG-022: snapshot of the query that was actually submitted. The
  // search query keys off THIS, not the live input, so typing after a search no
  // longer refires a search on every keystroke.
  const [submittedQuery, setSubmittedQuery] = useState('')
  const [sort, setSort] = useState('relevance')
  const [page, setPage] = useState(0)
  const [jurisdictionFilter, setJurisdictionFilter] = useState('')
  const [comparePatentId, setComparePatentId] = useState<string | null>(null)
  // BUG-095: two-step compare selection — project, then the actual document.
  const [compareProjectId, setCompareProjectId] = useState('')
  const [compareDocId, setCompareDocId] = useState('')
  const [comparing, setComparing] = useState(false)
  const [compareError, setCompareError] = useState('')
  const [compareResult, setCompareResult] = useState<NoveltyReport | null>(null)

  // Inline detail preview (right rail) — separate from the full /patents/{id}
  // page below, and separate from the card-click-navigates behaviour.
  const [previewPatentId, setPreviewPatentId] = useState<string | null>(null)

  // Assignee + date-range facets. Neither browsePatents nor searchPatents
  // support an assignee filter server-side, and browsePatents has no date
  // filter at all (only searchPatents does) — so both are applied client-side
  // to the currently loaded page of real results, which keeps them honest
  // (no server round-trip pretending to be more than it is) at the cost of
  // only filtering within the loaded page rather than the whole corpus.
  const [selectedAssignees, setSelectedAssignees] = useState<string[]>([])
  const [assigneeQuery, setAssigneeQuery] = useState('')
  const [dateFromFilter, setDateFromFilter] = useState('')
  const [dateToFilter, setDateToFilter] = useState('')

  const clearFacetFilters = () => {
    setSelectedAssignees([])
    setAssigneeQuery('')
    setDateFromFilter('')
    setDateToFilter('')
  }

  // Task #3326-r1-fix: render a detail panel when the URL is /patents/{id}.
  // The /patents/* route in App.tsx routes both /patents and /patents/{id} here,
  // so we read the trailing segment ourselves.
  const navigateForDetail = useNavigate()
  const location = useLocation()
  const detailPatentId = location.pathname.startsWith('/patents/')
    ? decodeURIComponent(location.pathname.slice('/patents/'.length).split('/')[0] || '')
    : ''
  // #4612/#4646: Details/Connections tab state for the patent record. MUST
  // stay at the top level (here, alongside the file's other ~15 hooks) and
  // NOT inside the `if (detailPatentId) { return (...) }` block below —
  // this component does not remount across list<->detail navigation
  // (App.tsx routes both /patents and /patents/* to the same element; a hook
  // placed inside that conditional would violate Rules-of-Hooks the first
  // time a user opens a patent, clicks an inventor, and returns to the list —
  // Red-Team Round 4 finding). A bare useState initializer does not re-fire
  // on id change since the instance persists, so the reset needs its own
  // effect, also at the top level.
  const [detailTab, setDetailTab] = useState<'details' | 'connections'>('details')
  useEffect(() => {
    setDetailTab('details')
  }, [detailPatentId])
  const {
    data: detailPatent,
    isLoading: detailLoading,
    isError: detailError,
  } = useQuery({
    queryKey: ['patent', detailPatentId],
    queryFn: () => getPatent(detailPatentId),
    enabled: !!detailPatentId,
    // Task #3326-r4 fix 1e: a 404 is a deterministic "not in local index"
    // signal, not a transient failure — surfacing the not-found banner
    // immediately is the right UX (no 7s retry stall before the error renders).
    retry: false,
  })

  const parentTopic = breadcrumb.length > 0 ? breadcrumb[breadcrumb.length - 1] : undefined
  const {
    data: topicData,
    isLoading: topicsLoading,
    isError: topicsError,
    refetch: refetchTopics,
  } = useQuery({
    queryKey: ['patent-topics', parentTopic],
    queryFn: () => getPatentTopics({ size: 50, parent: parentTopic }),
  })
  // BUG-VIS-4-2: bound the sidebar's loading state — the OpenSearch topic
  // aggregation is normally fast, but if it ever genuinely stalls, don't
  // leave the skeleton (or the header count) spinning forever.
  const topicsTimedOut = useTimedOut(topicsLoading, 8000)
  // Header count is the *global* index size, not the parent-scoped count.
  // When the user drills into a topic, topicData.total_patents shrinks to that
  // topic's hit count; we keep the unscoped total around so "X patents indexed"
  // stays meaningful as overall coverage rather than the current view.
  const {
    data: rootTopicData,
    isLoading: rootTopicsLoading,
    isError: rootTopicsError,
  } = useQuery({
    queryKey: ['patent-topics-root'],
    queryFn: () => getPatentTopics({ size: 50 }),
    staleTime: 5 * 60 * 1000,
  })
  const rootTopicsTimedOut = useTimedOut(rootTopicsLoading, 8000)

  const { data: browseData, isLoading: browseLoading, isError: browseError } = useQuery({
    queryKey: ['patent-browse', selectedTopic, sort, page, jurisdictionFilter],
    queryFn: () => browsePatents({
      topic: selectedTopic, size: 20, from: page * 20, sort,
      ...(jurisdictionFilter && { jurisdiction: jurisdictionFilter }),
    }),
    enabled: !!selectedTopic && !searchSubmitted,
  })

  const { data: searchData, isLoading: searchLoading, isError: searchError } = useQuery({
    // BUG-155/BUG-022: key on the SUBMITTED snapshot, not the live input.
    // BUG-143/BUG-015: the search backend (`_handle_search`) returns a single
    // ranked page with no `from`/offset, so fetch a larger page and paginate it
    // client-side (see the `page` slicing below).
    // #4612: server-side pagination — fetch one page (20) at from=page*20 and
    // read the TRUE match count from the response, instead of capping at 100.
    queryKey: ['patent-search', submittedQuery, inventorFilter, assigneeSearchFilter, page],
    queryFn: () => searchPatents(
      inventorFilter
        ? { inventor: inventorFilter, max_results: 20, from: page * 20 }
        : assigneeSearchFilter
        ? { assignee: assigneeSearchFilter, max_results: 20, from: page * 20 }
        : { query: submittedQuery, max_results: 20, from: page * 20 }),
    enabled: searchSubmitted && (!!submittedQuery || !!inventorFilter || !!assigneeSearchFilter),
  })

  const { data: projects } = useQuery({
    queryKey: ['projects'],
    queryFn: getProjects,
    enabled: !!comparePatentId,
  })

  // BUG-095: the compare endpoint requires a real document_id. Load the selected
  // project's documents so the user can pick the actual invention document (the
  // old modal listed PROJECTS and sent the project_id AS the document_id).
  const { data: compareDocuments, isLoading: compareDocsLoading } = useQuery({
    queryKey: ['documents', compareProjectId],
    queryFn: () => getDocuments(compareProjectId),
    enabled: !!comparePatentId && !!compareProjectId,
  })

  const { data: previewPatent, isLoading: previewLoading, isError: previewError } = useQuery({
    queryKey: ['patent-preview', previewPatentId],
    queryFn: () => getPatent(previewPatentId as string),
    enabled: !!previewPatentId,
    retry: false,
  })

  const openPreview = (patentId: string) => setPreviewPatentId(patentId)
  const closePreview = () => setPreviewPatentId(null)

  const handleTopicSelect = (topic: string) => {
    setCategoriesOpen(false)
    setSelectedTopic(topic)
    setPage(0)
    setSearchSubmitted(false)
    setInventorFilter('')
    setAssigneeSearchFilter('')
    setJurisdictionFilter('')
    clearFacetFilters()
    setPreviewPatentId(null)
  }

  // #4612/#4646: browse a CPC category from a Connections-graph cpc node —
  // navigate to the list view (categories only render there) and select it
  // the same way clicking a sidebar category does.
  const handleBrowseCategory = (label: string) => {
    handleTopicSelect(label)
    navigateForDetail('/patents')
  }

  const handleBack = () => {
    setBreadcrumb((prev) => prev.slice(0, -1))
    setSelectedTopic('')
    setPage(0)
    setJurisdictionFilter('')
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    const q = searchQuery.trim()
    if (q) {
      // BUG-155/BUG-022: snapshot the submitted query; the search query keys off
      // this, not the live input.
      setSubmittedQuery(q)
      setInventorFilter('')
      setAssigneeSearchFilter('')
      setSearchSubmitted(true)
      setPage(0)
      clearFacetFilters()
      setPreviewPatentId(null)
    }
  }

  const handleClearSearch = () => {
    setSearchQuery('')
    setSubmittedQuery('')
    setInventorFilter('')
    setAssigneeSearchFilter('')
    setSearchSubmitted(false)
    setPage(0)
    clearFacetFilters()
    setPreviewPatentId(null)
  }

  // #4612: run an "other patents by this inventor" search from a click on any
  // inventor name (card, preview, or full record).
  const handleInventorSearch = (name: string) => {
    const n = (name || '').trim()
    if (!n) return
    setInventorFilter(n)
    setAssigneeSearchFilter('')
    setSubmittedQuery('')
    setSearchQuery('')
    setSelectedTopic('')
    setSearchSubmitted(true)
    setPage(0)
    clearFacetFilters()
    setPreviewPatentId(null)
    navigateForDetail('/patents')
  }

  // #4612/#4646: "other patents by this assignee" — same pattern as
  // handleInventorSearch, backed by the real server-side assignee search.
  const handleAssigneeSearch = (name: string) => {
    const n = (name || '').trim()
    if (!n) return
    setAssigneeSearchFilter(n)
    setInventorFilter('')
    setSubmittedQuery('')
    setSearchQuery('')
    setSelectedTopic('')
    setSearchSubmitted(true)
    setPage(0)
    clearFacetFilters()
    setPreviewPatentId(null)
    navigateForDetail('/patents')
  }

  const openCompare = (patentId: string) => {
    setComparePatentId(patentId)
    setCompareProjectId('')
    setCompareDocId('')
    setCompareError('')
    setCompareResult(null)
  }

  const closeCompare = () => {
    setComparePatentId(null)
    setCompareProjectId('')
    setCompareDocId('')
    setCompareError('')
    setCompareResult(null)
    setComparing(false)
  }

  const handleCompare = async () => {
    // BUG-095: compareDocId is now a real document_id (from getDocuments), not a
    // project_id. The returned report is captured and rendered, and errors are
    // surfaced in the modal instead of being swallowed to the console.
    if (!comparePatentId || !compareDocId) return
    setComparing(true)
    setCompareError('')
    setCompareResult(null)
    try {
      const report = await compareWithInvention(comparePatentId, compareDocId)
      setCompareResult(report)
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } }; message?: string }
      setCompareError(e?.response?.data?.error || e?.message || 'Comparison failed. Please try again.')
    } finally {
      setComparing(false)
    }
  }

  // Normalize search results to PatentBrowseResult shape for PatentCard
  const toPatentBrowseResult = (r: PatentSearchResult): PatentBrowseResult => ({
    _id: r.patent_id,
    _score: r.score ?? null,
    patent_id: r.patent_id,
    title: r.title,
    abstract: r.abstract,
    cpc_codes: r.cpc_codes,
    topics: [],
    jurisdiction: r.jurisdiction,
    filing_date: r.filing_date,
    publication_date: r.publication_date,
    inventors: r.inventors,
    assignee: r.assignee,
    source_api: r.source_api,
    ...(r.google_patents_url ? { google_patents_url: r.google_patents_url } : {}),
    ...(r.source_lang ? { source_lang: r.source_lang } : {}),
  })

  // BUG-RETEST-C (#3441): suppress local-corpus misclassified entries (invention
  // files indexed as patents during local corpus ingestion runs).
  // These have source_api === 'local' and patent_id prefixed 'local:'.
  const _isLocalCorpus = (p: PatentBrowseResult) =>
    p.source_api === 'local' || p.patent_id?.startsWith('local:') || p.jurisdiction === 'local-corpus'
  const allPatents: PatentBrowseResult[] = (searchSubmitted
    ? (searchData?.results || []).map(toPatentBrowseResult)
    : browseData?.patents || []
  ).filter((p) => !_isLocalCorpus(p))
  // #4612: search + browse are BOTH server-paginated now (one page per fetch),
  // so no client-side slicing — the page IS what the server returned, and the
  // count is the TRUE match total (no silent cap at 100).
  const patents: PatentBrowseResult[] = allPatents
  const totalPatents = searchSubmitted
    ? (searchData?.total ?? allPatents.length)
    : browseData?.total
  const isLoading = searchSubmitted ? searchLoading : browseLoading
  const jurisdictions: JurisdictionFacet[] = browseData?.jurisdictions || []

  // "% match" badges are computed from the real relevance score already on
  // each result, normalised against the top score IN THIS PAGE — the backend
  // returns raw BM25/cosine scores with no fixed 0-100 scale, so an absolute
  // percentage would be meaningless. This is a real transform of real data,
  // never a fabricated number, and is only shown where a score exists.
  const maxPatentScore = useMemo(
    () => patents.reduce((max, p) => (p._score != null && p._score > max ? p._score : max), 0),
    [patents],
  )
  const matchPercentFor = (p: PatentBrowseResult): number | null => {
    if (p._score == null || maxPatentScore <= 0) return null
    return Math.min(100, Math.max(0, Math.round((p._score / maxPatentScore) * 100)))
  }

  // Assignee facet — built from the currently loaded page of real results
  // (neither browsePatents nor searchPatents accept an assignee filter
  // server-side), so this narrows what's shown on THIS page rather than the
  // whole corpus.
  const assigneeFacets = useMemo(() => {
    const counts = new Map<string, number>()
    for (const p of patents) {
      if (p.assignee) counts.set(p.assignee, (counts.get(p.assignee) || 0) + 1)
    }
    return Array.from(counts.entries())
      .map(([assignee, count]) => ({ assignee, count }))
      .sort((a, b) => b.count - a.count)
  }, [patents])

  const visiblePatents = useMemo(() => {
    return patents.filter((p) => {
      if (selectedAssignees.length > 0 && !selectedAssignees.includes(p.assignee)) return false
      if (assigneeQuery.trim() && !p.assignee?.toLowerCase().includes(assigneeQuery.trim().toLowerCase())) return false
      const filingYear = p.filing_date ? p.filing_date.slice(0, 4) : ''
      if (dateFromFilter && (!filingYear || filingYear < dateFromFilter)) return false
      if (dateToFilter && (!filingYear || filingYear > dateToFilter)) return false
      return true
    })
  }, [patents, selectedAssignees, assigneeQuery, dateFromFilter, dateToFilter])

  const facetFiltersActive = selectedAssignees.length > 0 || !!assigneeQuery.trim() || !!dateFromFilter || !!dateToFilter

  // Task #3326-r1-fix: detail panel when arriving via /patents/{id}
  if (detailPatentId) {
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigateForDetail('/patents')}
            className="flex items-center gap-1 text-sm text-iflow-600 hover:text-iflow-700"
          >
            <ArrowLeft size={14} /> Back to patents
          </button>
          <span className="text-xs font-mono text-gray-500">{detailPatentId}</span>
        </div>
        {detailLoading && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <LoadingSkeleton />
          </div>
        )}
        {(detailError || (!detailLoading && !detailPatent)) && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700 flex items-start gap-2">
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">Patent {detailPatentId} not found in local index.</p>
              <p className="text-xs text-red-600 mt-1">
                The local patent-embeddings index does not contain a record for this id.
                It may not have been ingested yet, or the id format may be incorrect.
                <button
                  type="button"
                  onClick={() => navigateForDetail('/patents')}
                  className="ml-2 underline hover:no-underline"
                >Back to patents</button>
              </p>
            </div>
          </div>
        )}
        {!detailLoading && !detailError && detailPatent && (
          <>
            {/* #4612/#4646: Details/Connections tab bar. Details renders the
                EXACT existing content below (title/abstract/cpc/inventors/
                dates/Google-Patents-link/Translate) verbatim, only wrapped in
                a detailTab==='details' conditional — nothing in that block
                changed. Connections swaps in the graph panel. */}
            <div className="flex gap-1 mb-4 border-b border-gray-200">
              <button
                type="button"
                onClick={() => setDetailTab('details')}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  detailTab === 'details'
                    ? 'border-iflow-600 text-iflow-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Details
              </button>
              <button
                type="button"
                onClick={() => setDetailTab('connections')}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  detailTab === 'connections'
                    ? 'border-iflow-600 text-iflow-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Connections
              </button>
            </div>
          </>
        )}
        {!detailLoading && !detailError && detailPatent && detailTab === 'connections' && (
          <PatentConnectionsPanel
            patentId={detailPatentId}
            onOpenPatent={(id) => navigateForDetail(`/patents/${encodeURIComponent(id)}`)}
            onInventorSearch={handleInventorSearch}
            onAssigneeSearch={handleAssigneeSearch}
            onBrowseCategory={handleBrowseCategory}
          />
        )}
        {!detailLoading && !detailError && detailPatent && detailTab === 'details' && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-mono text-iflow-600 bg-iflow-50 px-2 py-0.5 rounded">
                    {detailPatent.patent_id}
                  </span>
                  {detailPatent.jurisdiction && (
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <Globe size={12} /> {detailPatent.jurisdiction}
                    </span>
                  )}
                </div>
                <h2 className="text-lg font-semibold text-gray-900">{detailPatent.title}</h2>
                {detailPatent.assignee && (
                  <p className="text-sm text-gray-500 mt-1">{detailPatent.assignee}</p>
                )}
              </div>
            </div>
            {detailPatent.abstract && (
              <div className="mt-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Abstract</h3>
                <p className="text-sm text-gray-700 whitespace-pre-line">{decodeEntities(detailPatent.abstract)}</p>
              </div>
            )}
            {detailPatent.cpc_codes && detailPatent.cpc_codes.length > 0 && (
              <div className="mt-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">CPC Codes</h3>
                <div className="flex flex-wrap gap-1">
                  {detailPatent.cpc_codes.map((c: string, i: number) => (
                    <span key={i} className="text-xs font-mono text-gray-700 bg-gray-100 px-2 py-0.5 rounded">{c}</span>
                  ))}
                </div>
              </div>
            )}
            {detailPatent.inventors && detailPatent.inventors.length > 0 && (
              <div className="mt-4">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Inventors</h3>
                <p className="text-sm text-gray-700">
                  {detailPatent.inventors.map((inv: string, i: number) => (
                    <span key={i}>
                      {i > 0 && ', '}
                      <button
                        type="button"
                        onClick={() => handleInventorSearch(inv)}
                        className="text-iflow-600 hover:underline"
                        title={`See other patents by ${inv}`}
                      >{inv}</button>
                    </span>
                  ))}
                </p>
              </div>
            )}
            <div className="mt-4 text-xs text-gray-500 flex gap-4">
              {detailPatent.filing_date && <span>Filed: {detailPatent.filing_date}</span>}
              {detailPatent.publication_date && <span>Published: {detailPatent.publication_date}</span>}
              {detailPatent.source_api && <span>Source: {detailPatent.source_api}</span>}
            </div>
            {googlePatentsUrl(detailPatent) && (
              <a
                href={googlePatentsUrl(detailPatent) as string}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-iflow-700 bg-iflow-50 rounded-lg hover:bg-iflow-100"
              >
                View full patent on Google Patents <ExternalLink size={14} />
              </a>
            )}
            <PatentTranslate
              patentId={detailPatent.patent_id}
              sourceLang={(detailPatent as { source_lang?: string }).source_lang}
              sourceTitle={detailPatent.title}
              sourceAbstract={detailPatent.abstract}
              sourceClaims={(detailPatent as { claims?: string[] }).claims}
            />
          </div>
        )}
      </div>
    )
  }

  // BUG-VIS-4-2: the count is real, live data \u2014 it must never render as a
  // bare "\u2014" while it's simply still in flight. Show an honest "Loading\u2026"
  // until we have a number, and only fall back to an "unavailable" label
  // once the query has genuinely timed out or errored.
  const patentsIndexedLabel = (() => {
    const total = rootTopicData?.total_patents
    const scoped = topicData?.total_patents
    if (parentTopic && scoped != null && total != null && scoped !== total) {
      return `${scoped.toLocaleString()} / ${total.toLocaleString()} patents indexed`
    }
    if (total != null) return `${total.toLocaleString()} patents indexed`
    if (scoped != null) return `${scoped.toLocaleString()} patents indexed`
    if (rootTopicsError || rootTopicsTimedOut) return 'Patent count unavailable'
    return 'Loading patents\u2026'
  })()

  // UX-030: below lg the topic list + facets live in a drawer; the same fragment
  // renders in the desktop column so the two can never drift apart.
  const sidebarContent = (
    <>
          <TopicSidebar
            topics={topicData?.topics || []}
            selectedTopic={selectedTopic}
            onSelect={handleTopicSelect}
            breadcrumb={breadcrumb}
            onBack={handleBack}
            isLoading={topicsLoading}
            timedOut={topicsTimedOut}
            isError={topicsError}
            onRetry={() => refetchTopics()}
          />
          {/* Sub-topics when browsing */}
          {browseData?.sub_topics && browseData.sub_topics.length > 0 && selectedTopic && (
            <div className="mt-4 bg-white rounded-lg border border-gray-200 p-4">
              <h4 className="text-xs font-semibold text-gray-700 mb-2">Related Categories</h4>
              <div className="space-y-0.5 max-h-48 overflow-y-auto">
                {[...browseData.sub_topics].sort(topicFacetComparator).slice(0, 15).map((t) => (
                  <button
                    key={t.topic}
                    onClick={() => {
                      setBreadcrumb((prev) => [...prev, selectedTopic])
                      setCategoriesOpen(false)
                      setSelectedTopic(t.topic)
                      setPage(0)
                      setJurisdictionFilter('')
                    }}
                    className="w-full flex items-center justify-between px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded"
                  >
                    <span className="truncate">{t.topic}</span>
                    <span className="text-[10px] text-gray-500 ml-1">{(t.count ?? 0).toLocaleString()}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* Jurisdiction filter */}
          {jurisdictions.length > 0 && selectedTopic && !searchSubmitted && (
            <div className="mt-4 bg-white rounded-lg border border-gray-200 p-4">
              <h4 className="text-xs font-semibold text-gray-700 mb-2">Jurisdiction</h4>
              <div className="space-y-0.5 max-h-48 overflow-y-auto">
                <button
                  onClick={() => setJurisdictionFilter('')}
                  className={`w-full flex items-center justify-between px-2 py-1 text-xs rounded ${
                    !jurisdictionFilter ? 'bg-iflow-50 text-iflow-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <span>All</span>
                </button>
                {jurisdictions.map((j) => (
                  <button
                    key={j.jurisdiction}
                    onClick={() => { setJurisdictionFilter(j.jurisdiction); setPage(0) }}
                    className={`w-full flex items-center justify-between px-2 py-1 text-xs rounded ${
                      jurisdictionFilter === j.jurisdiction
                        ? 'bg-iflow-50 text-iflow-700 font-medium'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <span>{j.jurisdiction}</span>
                    <span className="text-[10px] text-gray-500 ml-1">{j.count.toLocaleString()}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* Date range facet — client-side (searchPatents supports date_from/
              date_to but browsePatents doesn't, so a single client-side filter
              over the loaded page works uniformly for both modes). */}
          {(selectedTopic || searchSubmitted) && (
            <div className="mt-4 bg-white rounded-lg border border-gray-200 p-4">
              <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                <CalendarRange size={12} /> Date range
              </h4>
              <p className="text-[10px] text-gray-500 mb-2">Filing year — narrows this page's results.</p>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  placeholder="From"
                  value={dateFromFilter}
                  onChange={(e) => setDateFromFilter(e.target.value)}
                  className="w-full px-2 py-1 text-xs light-input outline-none"
                  data-testid="patent-date-from-filter"
                  aria-label="Filing year from"
                />
                <span className="text-gray-500 text-xs">to</span>
                <input
                  type="number"
                  placeholder="To"
                  value={dateToFilter}
                  onChange={(e) => setDateToFilter(e.target.value)}
                  className="w-full px-2 py-1 text-xs light-input outline-none"
                  data-testid="patent-date-to-filter"
                  aria-label="Filing year to"
                />
              </div>
            </div>
          )}
          {/* Assignee facet — client-side, same reasoning as date range. Only
              worth showing once there's more than one distinct assignee to
              actually distinguish between. */}
          {(selectedTopic || searchSubmitted) && assigneeFacets.length > 1 && (
            <div className="mt-4 bg-white rounded-lg border border-gray-200 p-4">
              <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                <Building2 size={12} /> Assignee
              </h4>
              <p className="text-[10px] text-gray-500 mb-2">Company or institution — narrows this page's results.</p>
              <input
                value={assigneeQuery}
                onChange={(e) => setAssigneeQuery(e.target.value)}
                placeholder="e.g. Samsung SDI"
                className="w-full mb-2 px-2 py-1 text-xs light-input outline-none"
                data-testid="patent-assignee-filter-input"
              />
              <div className="space-y-0.5 max-h-40 overflow-y-auto">
                {assigneeFacets.slice(0, 15).map(({ assignee, count }) => (
                  <label key={assignee} className="flex items-center gap-2 px-1 py-1 text-xs text-gray-600 hover:bg-gray-50 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedAssignees.includes(assignee)}
                      onChange={() => setSelectedAssignees((prev) =>
                        prev.includes(assignee) ? prev.filter((a) => a !== assignee) : [...prev, assignee],
                      )}
                      className="rounded text-iflow-600"
                    />
                    <span className="truncate flex-1">{assignee}</span>
                    <span className="text-[10px] text-gray-500">{count}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
    </>
  )

  return (
    <div>
      <PageHeader
        title="Patents"
        description="Search worldwide patents for prior art, then save results to a list or compare them against one of your invention documents."
        helpKey="patents"
        status={{ label: patentsIndexedLabel, tone: 'neutral' }}
        secondaryActions={
          <button
            onClick={() => navigateForDetail('/lists')}
            className="px-3 py-2 text-sm font-medium text-iflow-700 bg-white border border-iflow-200 rounded-lg hover:bg-iflow-50 flex items-center gap-1.5"
          >
            <Bookmark size={14} /> Saved Lists
          </button>
        }
      />

      <div className="mb-6">
        <ContextualTip id="patents-intro">
          Bookmark any result to add it to a list, or use the compare action on a card to score a patent&rsquo;s novelty against one of your invention documents.
        </ContextualTip>
      </div>

      {/* Search bar */}
      <form onSubmit={handleSearch} className="mb-6">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); if (!e.target.value) setSearchSubmitted(false) }}
              placeholder="Search patents..."
              className="w-full pl-9 pr-3 py-2 light-input text-sm outline-none"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-iflow-700 text-white text-sm font-medium rounded-lg hover:bg-iflow-800 transition-colors"
          >
            Search
          </button>
          {searchSubmitted && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Clear
            </button>
          )}
        </div>
      </form>

      {(topicsError || browseError || searchError) && (
        <div className="mb-4 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <AlertCircle size={16} className="flex-shrink-0" />
          <span>Failed to load {topicsError ? 'patent categories' : searchError ? 'search results' : 'patents'}. The index may be temporarily unavailable.</span>
        </div>
      )}

      {/* UX-030: the topic list is a sidebar only from lg up; on phones and tablets
          it opens from this button instead. */}
      <div className="lg:hidden mb-4">
        <button
          type="button"
          onClick={() => setCategoriesOpen(true)}
          aria-haspopup="dialog"
          className="flex items-center gap-2 text-sm font-medium text-gray-700 px-4 py-2 border border-gray-200 rounded-lg bg-white hover:bg-gray-50"
        >
          <Filter size={16} />
          Categories / Filters
          {selectedTopic && (
            <span className="max-w-[10rem] truncate rounded-full bg-iflow-50 px-2 py-0.5 text-xs text-iflow-700">{selectedTopic}</span>
          )}
        </button>
      </div>
      {categoriesOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Categories and filters">
          <div className="absolute inset-0 bg-black/30" onClick={() => setCategoriesOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-80 max-w-[90vw] bg-white shadow-xl p-5 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-900">Categories / Filters</h3>
              <button type="button" onClick={() => setCategoriesOpen(false)} aria-label="Close categories and filters" className="text-gray-500 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            {sidebarContent}
          </div>
        </div>
      )}

      <div className="flex gap-6">
        {/* Topic sidebar — desktop column. Below lg the same content opens in the
            Categories / Filters drawer (UX-030). */}
        <div className="hidden lg:block w-64 flex-shrink-0">{sidebarContent}</div>

        {/* Patent list */}
        <div className="flex-1 min-w-0">
          {selectedTopic || searchSubmitted ? (
            <>
              <div className="flex items-center justify-between mb-4">
                <div>
                  {selectedTopic && !searchSubmitted && (
                    <h2 className="text-lg font-semibold text-gray-900">{selectedTopic}</h2>
                  )}
                  {searchSubmitted && inventorFilter && (
                    <h2 className="text-lg font-semibold text-gray-900">Patents by {inventorFilter}</h2>
                  )}
                  {searchSubmitted && !inventorFilter && assigneeSearchFilter && (
                    <h2 className="text-lg font-semibold text-gray-900">Patents by {assigneeSearchFilter}</h2>
                  )}
                  {searchSubmitted && !inventorFilter && !assigneeSearchFilter && (
                    <h2 className="text-lg font-semibold text-gray-900">Results for &ldquo;{submittedQuery}&rdquo;</h2>
                  )}
                  <p className="text-sm text-gray-500">
                    {totalPatents?.toLocaleString() || 0} patents
                    {facetFiltersActive && visiblePatents.length !== patents.length && (
                      <> &middot; {visiblePatents.length} shown on this page</>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {facetFiltersActive && (
                    <button type="button" onClick={clearFacetFilters} className="text-xs text-iflow-600 hover:text-iflow-700 underline">
                      Clear filters
                    </button>
                  )}
                  {!searchSubmitted && (
                    <div className="flex items-center gap-2">
                      <SortAsc size={14} className="text-gray-500" />
                      <select
                        value={sort}
                        onChange={(e) => { setSort(e.target.value); setPage(0) }}
                        className="px-2 py-1 text-xs light-input outline-none"
                      >
                        <option value="relevance">Relevance</option>
                        <option value="newest">Newest First</option>
                        <option value="oldest">Oldest First</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>

              {/* BUG-NEW-V (Round 6): suppress the skeleton when the
                  active query (browse for category-mode, search for
                  search-mode) has errored, so the skeleton can no
                  longer "persist" while a failed query is sitting
                  there. The page-level error banner at line 367
                  already explains the failure to the user — this
                  guard just stops the loader-state from masking it. */}
              {(searchSubmitted ? searchError : browseError) ? (
                <EmptyState
                  icon={FileSearch}
                  title="Patents unavailable"
                  description="The patents service did not respond. Try refreshing or switching to a different category."
                />
              ) : isLoading ? (
                <LoadingSkeleton rows={5} type="list" />
              ) : patents && patents.length > 0 ? (
                <div className="space-y-3">
                  {visiblePatents.length === 0 ? (
                    <div className="text-sm text-gray-500 py-6 text-center border border-dashed border-gray-200 rounded-lg">
                      No patents on this page match the selected filters.{' '}
                      <button type="button" onClick={clearFacetFilters} className="text-iflow-600 underline">
                        Clear filters
                      </button>
                    </div>
                  ) : (
                    visiblePatents.map((p) => (
                      <PatentCard
                        key={p._id || p.patent_id}
                        patent={p}
                        onCompare={openCompare}
                        onPreview={openPreview}
                        onInventorClick={handleInventorSearch}
                        matchPercent={matchPercentFor(p)}
                      />
                    ))
                  )}
                  {/* Pagination */}
                  {totalPatents != null && totalPatents > 20 && (
                    <div className="flex items-center justify-between pt-4">
                      <button
                        onClick={() => setPage((pg) => Math.max(0, pg - 1))}
                        disabled={page === 0}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <span className="text-xs text-gray-500">
                        {/* #4612: OpenSearch caps from+size at 10,000, so deep
                            paging stops there even though the header shows the
                            true total. */}
                        Page {page + 1} of {Math.ceil(Math.min(totalPatents, 10000) / 20).toLocaleString()}
                      </span>
                      <button
                        onClick={() => setPage((pg) => pg + 1)}
                        disabled={(page + 1) * 20 >= Math.min(totalPatents, 10000)}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <EmptyState
                  icon={FileSearch}
                  title="No patents found"
                  description="Try a different category or search term."
                />
              )}
            </>
          ) : (
            <EmptyState
              icon={FileSearch}
              title="Browse patents by category"
              description="Search above, or open Categories to browse by patent category (CPC)."
            />
          )}
        </div>

        {/* Inline detail preview — right rail, shown after clicking a card's
            Preview action. Does not navigate away, unlike the title click. */}
        {previewPatentId && (
          <PatentPreviewPanel
            patentId={previewPatentId}
            patent={previewPatent}
            isLoading={previewLoading}
            isError={previewError}
            onClose={closePreview}
            onOpenFull={() => navigateForDetail(`/patents/${encodeURIComponent(previewPatentId)}`)}
          />
        )}
      </div>

      {/* Compare Modal — BUG-095: pick a project, then a real document, run the
          comparison, render the returned report, and surface errors inline. */}
      {comparePatentId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Compare with Invention</h2>
              <button onClick={closeCompare} className="text-gray-500 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Pick one of your documents to compare against patent{' '}
              <span className="font-mono text-xs">{comparePatentId}</span>.
            </p>

            {/* Step 1 — project */}
            <label className="block text-xs font-medium text-gray-500 mb-1">Project</label>
            <select
              data-testid="compare-project-select"
              value={compareProjectId}
              onChange={(e) => { setCompareProjectId(e.target.value); setCompareDocId(''); setCompareResult(null); setCompareError('') }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-4"
            >
              <option value="">Select a project...</option>
              {projects ? disambiguateProjectLabels(projects).map((p) => (
                <option key={p.project_id} value={p.project_id}>{p.displayName}</option>
              )) : null}
            </select>

            {/* Step 2 — the actual document within that project */}
            <label className="block text-xs font-medium text-gray-500 mb-1">Document</label>
            <select
              data-testid="compare-document-select"
              value={compareDocId}
              onChange={(e) => { setCompareDocId(e.target.value); setCompareResult(null); setCompareError('') }}
              disabled={!compareProjectId || compareDocsLoading}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-2 disabled:bg-gray-50 disabled:text-gray-500"
            >
              <option value="">
                {!compareProjectId
                  ? 'Select a project first...'
                  : compareDocsLoading
                    ? 'Loading documents...'
                    : (compareDocuments && compareDocuments.length > 0)
                      ? 'Select a document...'
                      : 'No documents in this project'}
              </option>
              {(compareDocuments || []).map((d) => (
                <option key={d.document_id} value={d.document_id}>{d.title || d.filename || d.document_id}</option>
              ))}
            </select>

            {compareError && (
              <p data-testid="compare-error" className="text-sm text-red-600 mb-3">{compareError}</p>
            )}

            {compareResult && (
              <div data-testid="compare-result" className="mb-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-500">Novelty vs this patent</span>
                  {typeof compareResult.overall_novelty_score === 'number' && (
                    <span className="text-sm font-bold text-gray-800">
                      {Math.round(compareResult.overall_novelty_score * 10) / 10}/100
                    </span>
                  )}
                </div>
                {compareResult.summary && (
                  <p className="text-xs text-gray-600">{compareResult.summary}</p>
                )}
                {compareDocId && (
                  <button
                    type="button"
                    onClick={() => navigateForDetail(`/document/${compareDocId}`)}
                    className="mt-2 text-xs font-medium text-iflow-600 hover:text-iflow-700 underline"
                  >
                    View full analysis
                  </button>
                )}
              </div>
            )}

            <div className="flex gap-2 justify-end">
              <button
                onClick={closeCompare}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {compareResult ? 'Close' : 'Cancel'}
              </button>
              <button
                onClick={handleCompare}
                disabled={!compareDocId || comparing}
                className="px-4 py-2 text-sm bg-iflow-600 text-white rounded-lg hover:bg-iflow-700 disabled:opacity-50"
              >
                {comparing ? 'Analysing...' : 'Run Novelty Analysis'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
